import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { MBMError } from '@errors/mbm-error';
import { environment } from '@environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Let the OIDC library manage its own requests (token endpoint, userinfo, discovery).
  // Intercepting these can overwrite the library's own Authorization header with a
  // stale/empty token, causing 401s that then trigger a broken re-auth cycle.
  if (req.url.startsWith(environment.authConfig.authority)) {
    return next(req);
  }

  return authService.getAccessToken().pipe(
    switchMap((token) => {
      const authReq = token
        ? req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          })
        : req;

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Token rejected by server — redirect to IdP for re-auth.
            // Mark as handled so downstream catch blocks don't show an error message.
            authService.login();
            const mbmError: MBMError = {
              statusCode: 401,
              handled: true,
              logError: false,
              originalError: error
            };
            return throwError(() => mbmError);
          } else if (error.status === 403) {
            const mbmError: MBMError = {
              statusCode: 403,
              handled: false,
              logError: true,
              originalError: error
            };
            return throwError(() => mbmError);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
